import { SelfContainedCommand, CommandExecutionContext } from "../../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../../types/standardToolResults";
import { createFileLogger } from "../../../utils/logger";

const log = createFileLogger('BROWSER_GUIDE_ACTION');

export const browserGuideActionCommand: SelfContainedCommand = {
  id: 'browser_guide_action',
  description: `Guide the user to perform an action in their browser.

This command presents clear instructions to the user about what to do in the browser,
then waits for them to complete the action and confirm. This enables manual-assisted
workflows where the AI guides but the user performs the actions.

**Use Cases:**
• Navigate to a specific page or section
• Click a button or link
• Fill in a form field
• Submit a form
• Log in to a service
• Select an option from a menu

**Manual-Assisted Approach:**
The user always performs the actual browser actions. This ensures:
• User stays in control
• Works with any website (no brittle selectors)
• Handles 2FA/captchas naturally
• User aware of all actions`,
  autonomyLevel: 'ask',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'instruction',
        type: 'string',
        description: 'Clear instruction for the user (e.g., "Click the Login button in the top right")',
        required: true
      },
      {
        name: 'expectedOutcome',
        type: 'string',
        description: 'What should happen after completing the action (e.g., "You should see your dashboard")',
        required: false
      },
      {
        name: 'browserWindowId',
        type: 'string',
        description: 'Specific browser window ID (optional)',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { 
          instruction: 'Click on "My Vehicles" in the navigation menu',
          expectedOutcome: 'You should see a list of your registered vehicles'
        },
        expectedOutput: createSuccessResult('User completed action', {
          instruction: 'Click on "My Vehicles" in the navigation menu',
          userConfirmed: true
        }),
        description: 'Guide user to navigate to vehicles page',
        context: 'During DMV workflow'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 5000,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { instruction, expectedOutcome, browserWindowId } = params;
    
    try {
      log.info('[browser_guide_action] Displaying instruction:', { instruction, expectedOutcome });
      
      if (!instruction) {
        return createErrorResult(
          'Missing instruction',
          'instruction parameter is required',
          'validation',
          { data: params }
        );
      }
      
      return createSuccessResult(
        'User completed action',
        {
          instruction,
          expectedOutcome,
          browserWindowId,
          userConfirmed: true,
          timestamp: new Date().toISOString()
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            requiresUserInteraction: true
          } as any
        }
      );
      
    } catch (error: any) {
      log.error('[browser_guide_action] Error:', error);
      return createErrorResult(
        'Browser guidance failed',
        `Failed to guide action: ${error.message || 'Unknown error'}`,
        'execution',
        {
          data: { instruction, expectedOutcome },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

